import { createSlice, createSelector } from "@reduxjs/toolkit";
import { authApi, usersApi } from "../api/generatedApis.js";
import { APP_CONFIG } from "../../constants/app.js";
import { RBACManager } from "../../utils/rbac.js";

// Initial state
const initialState = {
  // User data
  user: null,
  token: localStorage.getItem(APP_CONFIG.TOKEN_KEY),
  refreshToken: localStorage.getItem(APP_CONFIG.REFRESH_TOKEN_KEY),

  // Authentication status
  isAuthenticated: false,
  isInitialized: false,

  // RBAC system
  rbac: new RBACManager(),

  // Session management
  lastActivity: Date.now(),
  sessionTimeout: APP_CONFIG.SESSION_TIMEOUT,

  // Security
  loginAttempts: 0,
  isLocked: false,
  lockoutTime: null,

  // UI state
  loading: false,
  error: null,

  // Settings
  rememberMe: false,
  preferences: {
    theme: "light",
    language: "en",
    notifications: true,
  },
};

// Authentication slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Manual logout action
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.rbac = new RBACManager();
      state.loginAttempts = 0;
      state.isLocked = false;
      state.lockoutTime = null;
      state.error = null;
      state.preferences = initialState.preferences;

      // Clear localStorage
      localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
      localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);
      localStorage.removeItem(APP_CONFIG.USER_KEY);

      console.log("👋 User logged out");
    },

    // Update last activity timestamp
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },

    // Handle login attempts and lockout
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1;

      // Lock account after 5 failed attempts
      if (state.loginAttempts >= 5) {
        state.isLocked = true;
        state.lockoutTime = Date.now() + 15 * 60 * 1000; // 15 minutes lockout
        console.warn("🔒 Account locked due to multiple failed login attempts");
      }
    },

    // Reset login attempts on successful login
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0;
      state.isLocked = false;
      state.lockoutTime = null;
    },

    // Check and handle lockout expiry
    checkLockoutStatus: (state) => {
      if (
        state.isLocked &&
        state.lockoutTime &&
        Date.now() > state.lockoutTime
      ) {
        state.isLocked = false;
        state.lockoutTime = null;
        state.loginAttempts = 0;
        console.log("🔓 Account lockout expired");
      }
    },

    // Update user preferences
    updatePreferences: (state, action) => {
      state.preferences = { ...state.preferences, ...action.payload };

      // Persist theme preference
      if (action.payload.theme) {
        document.documentElement.setAttribute(
          "data-bs-theme",
          action.payload.theme
        );
        localStorage.setItem("theme", action.payload.theme);
      }
    },

    // Set remember me preference
    setRememberMe: (state, action) => {
      state.rememberMe = action.payload;
    },

    // Clear error state
    clearError: (state) => {
      state.error = null;
    },

    // Set initialization complete
    setInitialized: (state) => {
      state.isInitialized = true;
      state.loading = false;
    },

    // Handle session expiry
    handleSessionExpiry: (state) => {
      console.warn("⏰ Session expired");

      // Keep user data but mark as not authenticated
      state.isAuthenticated = false;
      state.token = null;
      state.refreshToken = null;
      state.error = "Session expired. Please login again.";

      // Clear tokens from storage
      localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
      localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);
    },
  },

  // extraReducers: (builder) => {
  //   builder
  //     // === LOGIN MUTATION HANDLERS ===
  //     .addMatcher(authApi.endpoints.login.matchPending, (state) => {
  //       state.loading = true;
  //       state.error = null;
  //     })
  //     .addMatcher(
  //       authApi.endpoints.login.matchFulfilled,
  //       (state, { payload }) => {
  //         state.loading = false;
  //         state.user = payload.user;
  //         state.token = payload.token;
  //         state.refreshToken = payload.refreshToken || null;
  //         state.isAuthenticated = true;
  //         state.isInitialized = true;
  //         state.error = null;

  //         // Initialize RBAC system
  //         state.rbac = new RBACManager(
  //           payload.user.role,
  //           payload.user.customPermissions || []
  //         );

  //         // Reset security counters
  //         state.loginAttempts = 0;
  //         state.isLocked = false;
  //         state.lockoutTime = null;
  //         state.lastActivity = Date.now();

  //         // Store in localStorage
  //         localStorage.setItem(APP_CONFIG.TOKEN_KEY, payload.token);
  //         localStorage.setItem(
  //           APP_CONFIG.USER_KEY,
  //           JSON.stringify(payload.user)
  //         );

  //         if (payload.refreshToken) {
  //           localStorage.setItem(
  //             APP_CONFIG.REFRESH_TOKEN_KEY,
  //             payload.refreshToken
  //           );
  //         }

  //         console.log("✅ Login successful:", payload.user.name);
  //       }
  //     )
  //     .addMatcher(
  //       authApi.endpoints.login.matchRejected,
  //       (state, { payload }) => {
  //         state.loading = false;
  //         state.error = payload?.message || "Login failed";
  //         state.loginAttempts += 1;

  //         // Handle account lockout
  //         if (state.loginAttempts >= 5) {
  //           state.isLocked = true;
  //           state.lockoutTime = Date.now() + 15 * 60 * 1000;
  //         }

  //         console.error("❌ Login failed:", state.error);
  //       }
  //     )

  //     // === LOGOUT MUTATION HANDLERS ===
  //     .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
  //       // Complete logout
  //       state.user = null;
  //       state.token = null;
  //       state.refreshToken = null;
  //       state.isAuthenticated = false;
  //       state.rbac = new RBACManager();
  //       state.error = null;

  //       // Clear localStorage
  //       localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
  //       localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);
  //       localStorage.removeItem(APP_CONFIG.USER_KEY);

  //       console.log("👋 Logout successful");
  //     })

  //     // === GET CURRENT USER HANDLERS ===
  //     .addMatcher(authApi.endpoints.getCurrentUser.matchPending, (state) => {
  //       if (!state.user) {
  //         state.loading = true;
  //       }
  //     })
  //     .addMatcher(
  //       authApi.endpoints.getCurrentUser.matchFulfilled,
  //       (state, { payload }) => {
  //         state.loading = false;
  //         state.user = payload;
  //         state.isAuthenticated = true;
  //         state.isInitialized = true;
  //         state.error = null;

  //         // Update RBAC system
  //         state.rbac = new RBACManager(
  //           payload.role,
  //           payload.customPermissions || []
  //         );

  //         // Update stored user data
  //         localStorage.setItem(APP_CONFIG.USER_KEY, JSON.stringify(payload));

  //         console.log("✅ User data updated:", payload.name);
  //       }
  //     )
  //     .addMatcher(
  //       authApi.endpoints.getCurrentUser.matchRejected,
  //       (state, { payload }) => {
  //         state.loading = false;
  //         state.isInitialized = true;

  //         // Handle 401 errors (invalid token)
  //         if (payload?.status === 401) {
  //           state.isAuthenticated = false;
  //           state.token = null;
  //           state.refreshToken = null;
  //           state.user = null;
  //           state.rbac = new RBACManager();

  //           // Clear invalid tokens
  //           localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
  //           localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);
  //           localStorage.removeItem(APP_CONFIG.USER_KEY);
  //         } else {
  //           state.error = payload?.message || "Failed to fetch user data";
  //         }
  //       }
  //     )

  //     // === TOKEN REFRESH HANDLERS ===
  //     .addMatcher(
  //       authApi.endpoints.refreshtoken.matchFulfilled,
  //       (state, { payload }) => {
  //         state.token = payload.token;
  //         state.refreshToken = payload.refreshToken || state.refreshToken;
  //         state.lastActivity = Date.now();

  //         // Update stored tokens
  //         localStorage.setItem(APP_CONFIG.TOKEN_KEY, payload.token);
  //         if (payload.refreshToken) {
  //           localStorage.setItem(
  //             APP_CONFIG.REFRESH_TOKEN_KEY,
  //             payload.refreshToken
  //           );
  //         }

  //         console.log("🔄 Token refreshed successfully");
  //       }
  //     )
  //     .addMatcher(authApi.endpoints.refreshtoken.matchRejected, (state) => {
  //       // Refresh failed - force logout
  //       state.isAuthenticated = false;
  //       state.token = null;
  //       state.refreshToken = null;
  //       state.user = null;
  //       state.rbac = new RBACManager();
  //       state.error = "Session expired. Please login again.";

  //       // Clear all stored data
  //       localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
  //       localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);
  //       localStorage.removeItem(APP_CONFIG.USER_KEY);

  //       console.warn("❌ Token refresh failed - logging out");
  //     })

  //     // === PASSWORD CHANGE HANDLERS ===
  //     .addMatcher(authApi.endpoints.changepassword.matchFulfilled, (state) => {
  //       state.error = null;
  //       console.log("✅ Password changed successfully");
  //     });
  // },
});

// Export actions
export const {
  logout,
  updateLastActivity,
  incrementLoginAttempts,
  resetLoginAttempts,
  checkLockoutStatus,
  updatePreferences,
  setRememberMe,
  clearError,
  setInitialized,
  handleSessionExpiry,
} = authSlice.actions;

// ===== SELECTORS =====

// Basic selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsInitialized = (state) => state.auth.isInitialized;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectRBAC = (state) => state.auth.rbac;
export const selectUserPreferences = (state) => state.auth.preferences;

// Advanced selectors
export const selectUserRole = createSelector(
  [selectUser],
  (user) => user?.role || null
);

export const selectUserPermissions = createSelector([selectRBAC], (rbac) =>
  rbac.getUserPermissions()
);

export const selectCanAccessRoute = createSelector(
  [selectRBAC, (state, routePermissions) => routePermissions],
  (rbac, routePermissions) => {
    if (!routePermissions || routePermissions.length === 0) return true;
    return rbac.hasAnyPermission(routePermissions);
  }
);

export const selectHasPermission = createSelector(
  [selectRBAC, (state, permission) => permission],
  (rbac, permission) => rbac.hasPermission(permission)
);

export const selectHasAnyPermission = createSelector(
  [selectRBAC, (state, permissions) => permissions],
  (rbac, permissions) => rbac.hasAnyPermission(permissions)
);

export const selectHasAllPermissions = createSelector(
  [selectRBAC, (state, permissions) => permissions],
  (rbac, permissions) => rbac.hasAllPermissions(permissions)
);

export const selectIsSessionExpired = createSelector([selectAuth], (auth) => {
  if (!auth.isAuthenticated) return false;
  const timeSinceActivity = Date.now() - auth.lastActivity;
  return timeSinceActivity > auth.sessionTimeout;
});

export const selectIsAccountLocked = createSelector([selectAuth], (auth) => {
  if (!auth.isLocked) return false;
  if (!auth.lockoutTime) return false;
  return Date.now() < auth.lockoutTime;
});

export const selectLockoutTimeRemaining = createSelector(
  [selectAuth],
  (auth) => {
    if (!auth.isLocked || !auth.lockoutTime) return 0;
    const remaining = auth.lockoutTime - Date.now();
    return Math.max(0, remaining);
  }
);

export const selectUserDisplayInfo = createSelector([selectUser], (user) => {
  if (!user) return null;

  return {
    name: user.name,
    email: user.email,
    role: user.role,
    avatar: user.avatar || null,
    initials: user.name
      ? user.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "??",
  };
});

// ===== THUNKS FOR COMPLEX OPERATIONS =====

/**
 * Initialize authentication state from stored data
 */
export const initializeAuth = () => (dispatch, getState) => {
  const storedToken = localStorage.getItem(APP_CONFIG.TOKEN_KEY);
  const storedUser = localStorage.getItem(APP_CONFIG.USER_KEY);

  if (storedToken && storedUser) {
    try {
      const user = JSON.parse(storedUser);

      // Dispatch get current user to verify token validity
      dispatch(authApi.endpoints.getCurrentUser.initiate());

      console.log("🔄 Initializing auth from stored data...");
    } catch (error) {
      console.error("❌ Failed to parse stored user data:", error);

      // Clear invalid data
      localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
      localStorage.removeItem(APP_CONFIG.USER_KEY);
      localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);

      dispatch(setInitialized());
    }
  } else {
    dispatch(setInitialized());
  }
};

/**
 * Handle automatic logout due to inactivity
 */
export const handleInactivityLogout = () => (dispatch) => {
  dispatch(handleSessionExpiry());

  // Show notification
  dispatch({
    type: "ui/addNotification",
    payload: {
      type: "warning",
      message: "You have been logged out due to inactivity.",
    },
  });

  // Redirect to login
  window.location.href = "/login";
};

/**
 * Check session validity and handle expiry
 */
export const checkSessionValidity = () => (dispatch, getState) => {
  const state = getState();
  const isExpired = selectIsSessionExpired(state);

  if (isExpired && state.auth.isAuthenticated) {
    dispatch(handleInactivityLogout());
  }
};

// ===== MIDDLEWARE INTEGRATION =====

/**
 * Activity tracking middleware
 */
export const activityMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  // Track user activity for certain actions
  const activityActions = [
    "users/",
    "dashboard/",
    "reports/",
    "settings/",
    "notifications/",
  ];

  if (activityActions.some((prefix) => action.type.startsWith(prefix))) {
    store.dispatch(updateLastActivity());
  }

  return result;
};

// Export reducer
export default authSlice.reducer;
